<template>
    <div class="card-payment">
        <button class="btn-primary" type="button" @click="show" :disabled="disabled">{{ buttonText }}</button>

        <transition name="card-payment-fade">
            <div class="card-payment-overlay" v-if="isOpen">
                <div class="card-payment-modal">
                    <close-icon @click="hide"></close-icon>

                    <h2 class="mb-2">{{ titleText }}</h2>

                    <transition name="card-payment-fade">
                        <div v-show="isLoaded">
                            <div class="p-4 my-2 border shadow bg-gray-100">
                                <p class="font-bold mb-2">Important Notes:</p>
                                <p class="mb-2">The order will be completed immediately following invoice creation.</p>
                                <p class="mb-2">The invoice will be emailed to the primary billing user on the account.</p>
                                <p>Ensure you advise the client that our payment terms are 7 days from receipt.</p>
                            </div>

                            <div class="form-field" v-if="amount">
                                <label>Amount</label>
                                <input type="text" class="form-field" readonly="readonly" :value="formattedAmount">
                            </div>

                            <template v-if="!savedCustomerId">
                                <div class="form-field">
                                    <label>Business Name</label>
                                    <companies-house-search
                                        :value="business.name"
                                        @change="populateDataFromCoHo"
                                        @input="(v) => business.name = v"
                                    ></companies-house-search>
                                    <p class="text-sm text-gray-600 mt-1">Start typing to search Companies House data.</p>
                                </div>

                                <div class="form-field">
                                    <label>Address (first line)</label>
                                    <input type="text" class="form-field" v-model="business.address.line1" placeholder="1 Any Street">
                                </div>

                                <div class="form-field">
                                    <label>Address (second line)</label>
                                    <input type="text" class="form-field" v-model="business.address.line2" placeholder="Anytown">
                                </div>

                                <div class="flex">
                                    <div class="w-full sm:w-1/2 sm:pr-2">
                                        <div class="form-field">
                                            <label>Postcode</label>
                                            <input type="text" class="form-field" v-model="business.address.postcode" placeholder="SW1A 1AA">
                                        </div>
                                    </div>
                                    <div class="w-full sm:w-1/2 sm:pr-2">

                                    </div>
                                </div>
                            </template>

                            <template v-if="savedCustomerId">
                                <div class="bg-gray-200 shadow-inner mt-4 p-4">
                                    <p class="mb-4">Customer already setup for invoicing. To change these details, please contact Accounts.</p>
                                    <address class="roman" v-html="businessDetailsHtml"></address>
                                </div>
                            </template>

                            <div class="text-center mt-6">
                                <p class="text-red-500 mb-2" v-if="error">{{ error }}</p>
                                <button type="button" class="btn-primary" @click="process" v-if="!isProcessing">{{ buttonText }}</button>
                                <button type="button" class="btn-primary muted" v-if="isProcessing">Please wait...</button>
                            </div>
                        </div>
                    </transition>

                    <div class="h-48" v-if="!isLoaded"></div>

                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import Loader from './Loader'
    import CompaniesHouseSearch from './CompaniesHouseSearch'

    export default {
        name: 'InvoicePayment',
        components: {
            CompaniesHouseSearch,
            Loader,
        },
        props: {
            amount: {
                type: Number,
                default: null,
            },
            disabled: {
                type: Boolean,
                default: null,
            },
            title: {
                type: String,
                default: 'Invoice Payment',
            },
            text: {
                type: String,
                default: 'Process Invoice Payment',
            },
            account: {
                type: Object,
                default: function () {
                    return {}
                },
            },
        },
        data() {
            return {
                open: false,
                error: null,
                loaded: false,
                processing: false,
                savedCustomerId: null,
                business: {
                    name: '',
                    address: {
                        line1: '',
                        line2: '',
                        postcode: '',
                    },
                },
            }
        },
        mounted() {
            //
        },
        methods: {
            /**
             * Open and show the invoice payment form
             *
             * @return {void}
             */
            show() {
                this.open = true
                this.initialize()
            },
            /**
             * Handle a close event
             *
             * @param  {Object} event
             * @return {void}
             */
            hide(event) {
                this.open = false
                this.error = null
                this.loaded = false
                this.processing = false
                this.savedCustomerId = false
                this.business = {
                    name: '',
                    address: {
                        line1: '',
                        line2: '',
                        postcode: '',
                    },
                }

                this.$emit('close')
            },
            /**
             * Initialise the payment form
             *
             * @return {void}
             */
            async initialize() {
                await this.loadCustomerData()
                    .then(() => {
                        this.loaded = true
                    })
                    .catch(() => {
                        this.loaded = true
                    })
            },
            /**
             * Load any customer data to pre-populate fields
             *
             * @return {Promise}
             */
            async loadCustomerData() {
                return new Promise((resolve, reject) => {
                    if (this.account && this.account.flags) {
                        if (this.account.flags['qbo_customer_id']) {
                            return this.loadQBOData()
                                .then(response => {
                                    this.savedCustomerId = response.data.Id
                                    this.business.name = response.data.CompanyName
                                    this.business.address.line1 = response.data.BillAddr.Line1
                                    this.business.address.line2 = response.data.BillAddr.Line2
                                    this.business.address.postcode = response.data.BillAddr.PostalCode

                                    return resolve()
                                })
                                .catch(e => {
                                    return reject()
                                })
                        }

                        if (this.account.flags['billing_company_name']) {
                            this.business.name = this.account.flags['billing_company_name']
                        }

                        return resolve()
                    }

                    return resolve()
                })
            },
            /**
             * Load the QBO data for the given account
             *
             * @return {void}
             */
            async loadQBOData() {
                return axios.get(route('accounts.qbo', this.account.id))
            },
            /**
             * Process a charge
             *
             * @return {void}
             */
            process(event) {
                event.preventDefault()
                this.error = null
                this.processing = true

                if (!this.business.name || !this.business.address.line1 || !this.business.address.postcode) {
                    this.error = 'Business name, Address (first line) and Postcode must be complete'
                    this.processing = false

                    return
                }

                let token = JSON.stringify(this.business)
                if (this.savedCustomerId) {
                    token = this.savedCustomerId
                }

                this.$emit('submit', token)
                this.hide()
            },
            /**
             * Populate business data from a Companies House record.
             */
            populateDataFromCoHo(data) {
                if (data === null) {
                    this.business.name = null
                    this.business.address.line1 = null
                    this.business.address.line2 = null
                    this.business.address.postcode = null

                    return
                }

                this.business.name = _.startCase(_.toLower(data.title))
                this.business.address.line1 =  _.trim(data.address.premises + ' ' + data.address.address_line_1)
                this.business.address.line2 = _.trim(data.address.locality)
                this.business.address.postcode = data.address.postal_code
            },
        },
        computed: {
            /**
             * Return the amount to charge formatted for display
             *
             * @return {String}
             */
            formattedAmount() {
                if (!this.amount) {
                    return null
                }

                return '£' + parseFloat(this.amount).toFixed(2)
            },
            /**
             * Determine if the form is currently processing
             *
             * @return {Boolean}
             */
            isProcessing() {
                return this.processing
            },
            /**
             * Determine if the form is loaded
             *
             * @return {Boolean}
             */
            isLoaded() {
                return !this.disabled && this.loaded
            },
            /**
             * Determine if the form is open
             *
             * @return {Boolean}
             */
            isOpen() {
                return !this.disabled && this.open
            },
            /**
             * Returns the text for the trigger button with a replacement
             * fpr the amount to be charged
             *
             * @return {String}
             */
            buttonText() {
                if (!this.amount) {
                    return this.text
                }

                return this.text.replace(':amount', this.amount.toFixed(2))
            },
            /**
             * Returns the text for the modal title with a replacement
             * fpr the amount to be charged
             *
             * @return {String}
             */
            titleText() {
                if (!this.amount) {
                    return this.title
                }

                return this.title.replace(':amount', this.amount.toFixed(2))
            },
            /**
             * Determine if there are any saved payment methods
             *
             * @return {Boolean}
             */
            hasSavedMethods() {
                return this.savedCards.length > 0
            },
            /**
             * Return the business details as a HTML string.
             *
             * @return {string}
             */
            businessDetailsHtml() {
                let details = []
                details.push(this.business.name)
                details.push(this.business.address.line1)
                details.push(this.business.address.line2)
                details.push(this.business.address.postcode)

                return details.filter(v => v && v.length > 0).join('<br>')
            },
        },
        watch: {
            //
        }
    }
</script>

<style lang="less">
.card-payment {
    display: inline-block;
    text-align: left;

    .card-payment-overlay {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: fixed;
        z-index: 9999998;
        background: rgba(100, 100, 100, 0.75);

        .card-payment-modal {
            top: 50%;
            left: 50%;
            width: 400px;
            padding: 15px;
            max-width: 100%;
            z-index: 9999999;
            position: absolute;
            background: #FFFFFF;
            transform: translate(-50%, -50%);
            box-shadow: 0 2px 4px rgba(100, 100, 100, 0.6);
            max-height: 95vh;
            overflow: auto;

            .close-icon {
                top: 15px;
                right: 15px;
                position: absolute;
            }

            .card-payment-loading {
                min-height: 100px;
                text-align: center;
            }
        }
    }
}

.card-payment-fade-enter-active {
    transition: all .3s;
    opacity: 1;
}
.card-payment-fade-enter {
    transition: all .3s;
    opacity: 0;
}
.card-payment-fade-leave-active {
    transition: all .3s;
    opacity: 0;
}
</style>
